import { assign, fromPromise, setup } from 'xstate';
import { showAxiosErrorAction } from '~/features/actions';
import type { ListingActivationPreview } from '~/features/employer/billing/types';
import { isPaymentMethodRequired } from '../../listing-search/errors';
import {
  fallbackListingFeePreview,
  listingFeeIsDue,
  showFeePreviewError,
  warnPossibleDuplicates,
} from '../listingFee';
import { jobListingAdditionalTermsMachine } from '../shared-steps/additional-terms/additionalTerms.machine';
import { jobListingFacilitiesMachine } from '../shared-steps/facilities/facilities.machine';
import {
  addDetail,
  addListing,
  closeModal,
  deleteListing,
  nextStep,
  openDeleteConfirmation,
  openModal,
  prevStep,
  resetLoading,
  resetStep,
  setListingFacilities,
  setListingFacilityStep,
  setListings,
  setListingStates,
  setListingToDelete,
  setListingToEdit,
  setLoading,
  updateDetail,
  updateListing,
} from './actions';
import {
  activateListing,
  API_CALLS,
  deactivateListing,
  getSummary,
  previewActivationFee,
} from './api';
import { Context, Events, Scope } from './types';
import { basicInfoMachine } from './wizard-steps/basic-info/basicInfo.machine';

// TODO: MCV-558 - Add back in as steps are added
import { compensationMachine } from './wizard-steps/compensation/compensation.machine';
import { laborMetricsMachine } from './wizard-steps/labor-metrics/laborMetrics.machine';
import { onCallShiftDetailsMachine } from './wizard-steps/on-call-shift-details/onCallShiftDetails.machine';
import { shiftDetailsMachine } from './wizard-steps/shift-details/shiftDetails.machine';

const WIZARD_STEPS = [
  '$_BASIC_INFO',
  '$_FACILITIES',
  '$_LABOR_METRICS',
  '$_COMPENSATION',
  '$_SHIFT_DETAILS',
  '$_ON_CALL_SHIFT_DETAILS',
  '$_COST_CALCULATOR',
  '$_ADDITIONAL_INFO',
  '$_REVIEW',
];

export const jobListingMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
  actions: {
    openModal: assign(openModal),
    closeModal: assign(closeModal),
    nextStep: assign(nextStep),
    prevStep: assign(prevStep),
    resetStep: assign(resetStep),
    setListings: assign(setListings),
    setListingToEdit: assign(setListingToEdit),
    setListingToDelete: assign(setListingToDelete),
    addJobListing: assign(addListing),
    updateJobListing: assign(updateListing),
    deleteJobListing: assign(deleteListing),
    setLoading: assign(setLoading),
    resetLoading: assign(resetLoading),
    setListingFacilityStep: assign(setListingFacilityStep),
    setListingStates: assign(setListingStates),
    setJobListingFacilities: assign(setListingFacilities),
    showError: showAxiosErrorAction,
    updateDetail: assign(updateDetail),
    addDetail: assign(addDetail),
    sendDeleteConfirmation: openDeleteConfirmation,
    // [MCV-1933] Listing-fee confirmation and the non-blocking duplicate warning.
    setFeePreview: assign({
      feePreview: ({ event }) => (event as unknown as { output: ListingActivationPreview }).output,
    }),
    clearFeePreview: assign({ feePreview: undefined }),
    markPaymentMethodMissing: assign({
      feePreview: ({ context }) => ({
        ...(context.feePreview ?? fallbackListingFeePreview(false)),
        paymentMethodOnFile: false,
      }),
    }),
    warnPossibleDuplicates,
    showFeePreviewError,
  },
  guards: {
    canSubmitListing: ({ context }) => Boolean(context.listing?.detail?.id),
  },
  actors: {
    basicInfoRef: basicInfoMachine,
    facilitiesStepRef: jobListingFacilitiesMachine,
    laborMetricsRef: laborMetricsMachine,
    compensationStepRef: compensationMachine,
    shiftDetailsRef: shiftDetailsMachine,
    onCallShiftDetailsRef: onCallShiftDetailsMachine,
    additionalTermsRef: jobListingAdditionalTermsMachine,
    getJobListings: fromPromise(API_CALLS.get),
    deleteJobListing: fromPromise(API_CALLS.delete),
    deactivateListing: fromPromise(deactivateListing),
    activateListing: fromPromise(activateListing),
    previewActivationFee: fromPromise(previewActivationFee),
    getSummary: fromPromise(getSummary),
  },
}).createMachine({
  context: ({ spawn, self, input }) => ({
    employerUser: undefined,
    currentStep: 0,
    parentRef: input?.parentRef,
    basicInfoRef: spawn('basicInfoRef', {
      id: 'jobListingBasicInfoState',
      input: { parentRef: self },
    }),
    facilitiesStepRef: spawn('facilitiesStepRef', {
      id: 'jobListingFacilitiesState',
      input: { parentRef: self },
    }),
    laborMetricsRef: spawn('laborMetricsRef', {
      id: 'jobListingLaborMetricsState',
      systemId: 'jobListingLaborMetricsState',
      input: { parentRef: self },
    }),
    compensationStepRef: spawn('compensationStepRef', {
      id: 'jobListingCompensationState',
      systemId: 'jobListingCompensationState',
      input: { parentRef: self },
    }),
    shiftDetailsRef: spawn('shiftDetailsRef', {
      id: 'jobListingShiftDetailsState',
      systemId: 'jobListingShiftDetailsState',
      input: { parentRef: self },
    }),
    onCallShiftDetailsRef: spawn('onCallShiftDetailsRef', {
      id: 'jobListingOnCallShiftDetailsState',
      systemId: 'jobListingOnCallShiftDetailsState',
      input: { parentRef: self },
    }),
    additionalTermsRef: spawn('additionalTermsRef', {
      id: 'jobAdditionalTermsState',
      input: { parentRef: self },
    }),
    listings: [],
    listing: undefined,
    loading: true,
    idToDelete: -1,
    modal: {
      edit: false,
      create: false,
    },
    summary: undefined,
    feePreview: undefined,
  }),
  initial: '$_VIEW',
  on: {
    ON_SET_EMPLOYER_USER: {
      actions: assign({
        employerUser: ({ event }) => event.payload.employerUser,
      }),
    },
    ON_SEND_DELETE_CONFIRMATION: {
      actions: 'sendDeleteConfirmation',
    },
    ON_DELETE: {
      target: '.$_DELETE',
      actions: 'setListingToDelete',
    },
    ON_LOAD: [
      {
        guard: ({ context, event }) =>
          !(event?.payload?.employerUser?.employerId ?? context.employerUser?.employerId),
        target: '.$_VIEW',
        actions: ['resetLoading'],
      },
      {
        target: '.$_LOAD',
        actions: [
          assign({
            employerUser: ({ context, event }) =>
              event?.payload?.employerUser ?? context.employerUser,
          }),
          'setLoading',
        ],
      },
    ],
    ON_SUBMIT_LISTING: {
      guard: 'canSubmitListing',
      target: '.$_FEE_PREVIEW',
      actions: 'setLoading',
    },
    ON_SAVE_DRAFT: {
      target: '.$_DEACTIVATE',
      actions: 'setLoading',
    },
    ON_CLOSE_MODAL: {
      target: '.$_LOAD',
      actions: 'closeModal',
    },
    ON_ADD_LISTING: {
      actions: 'addJobListing',
    },
    ON_UPDATE_LISTING: {
      actions: 'updateJobListing',
    },
    ON_UPDATE_FACILITIES: {
      actions: 'setJobListingFacilities',
    },
    ON_UPDATE_FACILITY_STEP: {
      actions: 'setListingFacilityStep',
    },
    ON_UPDATE_STATES: {
      actions: 'setListingStates',
    },
    ON_ADD_DETAIL: {
      actions: 'addDetail',
    },
    ON_UPDATE_DETAIL: {
      actions: 'updateDetail',
    },
  },
  states: {
    $_VIEW: {
      on: {
        ON_OPEN_MODAL: {
          target: '$_BASIC_INFO',
          actions: 'openModal',
        },
        ON_EDIT: {
          target: '$_BASIC_INFO',
          actions: ['openModal', 'setListingToEdit'],
        },
      },
    },
    // [MCV-1933] Ask what publishing costs before activating. Only an explicit charge interrupts;
    // a failed preview returns to review without publishing or charging.
    $_FEE_PREVIEW: {
      invoke: {
        id: 'previewFee',
        src: 'previewActivationFee',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: [
          {
            guard: ({ event }) => listingFeeIsDue(event.output),
            target: '$_FEE_CONFIRM',
            actions: ['setFeePreview', 'resetLoading'],
          },
          { target: '$_ACTIVATE' },
        ],
        onError: { target: '$_REVIEW', actions: ['resetLoading', 'showFeePreviewError'] },
      },
    },
    $_FEE_CONFIRM: {
      on: {
        ON_CONFIRM_FEE: {
          guard: ({ context }) => context.feePreview?.paymentMethodOnFile !== false,
          target: '$_ACTIVATE',
          actions: 'setLoading',
        },
        ON_CANCEL_FEE: {
          target: '$_REVIEW',
          actions: ['clearFeePreview', 'resetLoading'],
        },
      },
    },
    $_ACTIVATE: {
      invoke: {
        id: 'activate',
        src: 'activateListing',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_LOAD',
          actions: ['closeModal', 'clearFeePreview', 'warnPossibleDuplicates'],
        },
        onError: [
          {
            // 402: a fee is due and there is no payment method (or it lapsed since the preview).
            guard: ({ event }) => isPaymentMethodRequired(event.error),
            target: '$_FEE_CONFIRM',
            actions: ['markPaymentMethodMissing', 'resetLoading'],
          },
          {
            target: '$_VIEW',
            actions: ['showError', 'clearFeePreview', 'resetLoading'],
          },
        ],
      },
    },
    $_DEACTIVATE: {
      invoke: {
        id: 'deactivate',
        src: 'deactivateListing',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_LOAD',
          actions: 'closeModal',
        },
        onError: {
          target: '$_VIEW',
          actions: ['showError', 'resetLoading'],
        },
      },
    },
    $_LOAD: {
      entry: ['setLoading'],
      invoke: {
        id: 'get',
        src: 'getJobListings',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_VIEW',
          actions: ['setListings', 'resetLoading'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['showError', 'resetLoading'],
        },
      },
    },
    $_DELETE: {
      invoke: {
        id: 'delete',
        src: 'deleteJobListing',
        input: ({ context, event }: Scope) => {
          return { context, event };
        },
        onDone: {
          target: '$_VIEW',
          actions: 'deleteJobListing',
        },
        onError: {
          target: '$_VIEW',
          actions: 'showError',
        },
      },
    },
    // STEP 1
    $_BASIC_INFO: {
      on: {
        ON_NEXT: {
          target: WIZARD_STEPS[1],
          actions: 'nextStep',
        },
        ON_PREV: {
          target: '$_LOAD',
          actions: 'closeModal',
        },
      },
    },
    // // STEP 2
    $_FACILITIES: {
      entry: [
        ({ context }) =>
          context.facilitiesStepRef.send({
            type: 'ON_LOAD',
            payload: { listingId: context.listing?.id || -1 },
          }),
      ],
      on: {
        ON_NEXT: {
          target: WIZARD_STEPS[2],
          actions: 'nextStep',
        },
        ON_PREV: {
          target: WIZARD_STEPS[0],
          actions: 'prevStep',
        },
      },
    },
    // // STEP 3
    $_LABOR_METRICS: {
      on: {
        ON_NEXT: {
          target: WIZARD_STEPS[3],
          actions: 'nextStep',
        },
        ON_PREV: {
          target: WIZARD_STEPS[1],
          actions: 'prevStep',
        },
      },
    },
    // // STEP 4
    $_COMPENSATION: {
      on: {
        ON_NEXT: {
          target: WIZARD_STEPS[4],
          actions: 'nextStep',
        },
        ON_PREV: {
          target: WIZARD_STEPS[2],
          actions: 'prevStep',
        },
      },
    },
    // // STEP 5
    $_SHIFT_DETAILS: {
      on: {
        ON_NEXT: {
          target: WIZARD_STEPS[5],
          actions: 'nextStep',
        },
        ON_PREV: {
          target: WIZARD_STEPS[3],
          actions: 'prevStep',
        },
        ON_SKIP: {
          // On edit, Skip intentionally leaves persisted shift fields unchanged.
          target: WIZARD_STEPS[5],
          actions: 'nextStep',
        },
      },
    },
    // STEP 6
    $_ON_CALL_SHIFT_DETAILS: {
      on: {
        ON_NEXT: {
          target: WIZARD_STEPS[6],
          actions: 'nextStep',
        },
        ON_PREV: {
          target: WIZARD_STEPS[4],
          actions: 'prevStep',
        },
        ON_SKIP: {
          target: WIZARD_STEPS[6],
          actions: 'nextStep',
        },
      },
    },
    // // STEP 7
    $_COST_CALCULATOR: {
      on: {
        ON_NEXT: {
          target: WIZARD_STEPS[7],
          actions: 'nextStep',
        },
        ON_PREV: {
          target: WIZARD_STEPS[5],
          actions: 'prevStep',
        },
      },
    },
    // // STEP 8
    $_ADDITIONAL_INFO: {
      on: {
        ON_NEXT: {
          target: WIZARD_STEPS[8],
          actions: 'nextStep',
        },
        ON_PREV: {
          target: WIZARD_STEPS[6],
          actions: 'prevStep',
        },
        ON_SKIP: {
          target: WIZARD_STEPS[8],
          actions: 'nextStep',
        },
      },
    },
    // STEP 9
    $_REVIEW: {
      invoke: {
        src: 'getSummary',
        input: ({ context }) => ({
          context,
          event: {
            type: 'ON_GET_SUMMARY',
            payload: { listing: context.listing?.detail },
          },
        }),
        onDone: {
          actions: [assign({ summary: ({ event }) => event.output })],
        },
        onError: {
          actions: ['showError', assign({ summary: () => undefined })],
        },
      },
      on: {
        ON_NEXT: {
          target: '$_VIEW',
          actions: ['resetStep', 'closeModal'],
        },
        ON_PREV: {
          target: WIZARD_STEPS[7],
          actions: 'prevStep',
        },
      },
    },
  },
});
